import type { ApiErrorCode, ApiErrorDetail, ApiErrorResponse } from '@/api/types'

/** Erreur normalisée : toute réponse non-2xx du backend est convertie en `ApiError`. */
export class ApiError extends Error {
  readonly status: number
  readonly code: ApiErrorCode
  readonly details: ApiErrorDetail[]
  /** Cas précis à l'intérieur du code, quand le serveur en nomme un. */
  readonly reason: string | null

  constructor(
    status: number,
    code: ApiErrorCode,
    message: string,
    details: ApiErrorDetail[] = [],
    reason: string | null = null,
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
    this.reason = reason
  }

  /** Erreur de champ associée à `body.<field>`, pour alimenter react-hook-form. */
  fieldError(field: string): string | undefined {
    return this.details.find((detail) => detail.loc.includes(field))?.msg
  }

  get isUnauthenticated(): boolean {
    return this.status === 401
  }

  /** Le serveur n'a jamais répondu : la session locale reste valide. */
  get isNetworkError(): boolean {
    return this.code === 'network_error'
  }

  /** La session est refusée par le serveur : il faut la vider. */
  get isAuthFailure(): boolean {
    return this.status === 401 || this.status === 403
  }

  get isForbidden(): boolean {
    return this.status === 403
  }

  get isNotFound(): boolean {
    return this.status === 404
  }

  get isConflict(): boolean {
    return this.status === 409
  }
}

const FALLBACK_MESSAGES: Record<number, string> = {
  400: 'Requête invalide.',
  401: 'Session expirée, veuillez vous reconnecter.',
  403: 'Vous n’avez pas les droits nécessaires pour cette action.',
  404: 'Ressource introuvable.',
  409: 'Conflit : cette opération entre en collision avec un état existant.',
  422: 'Les données saisies sont invalides.',
  429: 'Trop de tentatives, réessayez dans quelques minutes.',
  500: 'Erreur interne.',
  502: 'Service injoignable.',
  503: 'Service temporairement indisponible.',
}

export function codeForStatus(status: number): ApiErrorCode {
  switch (status) {
    case 400:
      return 'bad_request'
    case 401:
      return 'unauthenticated'
    case 403:
      return 'forbidden'
    case 404:
      return 'not_found'
    case 405:
      return 'method_not_allowed'
    case 409:
      return 'conflict'
    case 422:
      return 'validation_error'
    case 429:
      return 'rate_limited'
    default:
      // Toute autre réponse du serveur, y compris 5xx. `network_error` est
      // réservé à l'absence totale de réponse (voir ApiError.isNetworkError).
      return 'internal_error'
  }
}

function isApiErrorBody(value: unknown): value is ApiErrorResponse {
  if (typeof value !== 'object' || value === null || !('error' in value)) return false
  const { error } = value
  return typeof error === 'object' && error !== null && 'message' in error
}

/** Construit une `ApiError` depuis une réponse HTTP, en tolérant un corps non conforme. */
export async function apiErrorFromResponse(response: Response): Promise<ApiError> {
  let body: unknown
  try {
    body = await response.json()
  } catch {
    body = undefined
  }

  if (isApiErrorBody(body)) {
    // Le corps vient du réseau : chaque champ est traité comme facultatif.
    const raw = body.error as Partial<ApiErrorResponse['error']>
    return new ApiError(
      response.status,
      raw.code ?? codeForStatus(response.status),
      raw.message ?? FALLBACK_MESSAGES[response.status] ?? 'Erreur inattendue.',
      raw.details ?? [],
      raw.reason ?? null,
    )
  }

  return new ApiError(
    response.status,
    codeForStatus(response.status),
    FALLBACK_MESSAGES[response.status] ?? `Erreur ${response.status}.`,
  )
}

/** Message affichable pour n’importe quelle exception remontée par le client API. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message
  if (error instanceof Error) return error.message
  return 'Erreur inattendue.'
}

/**
 * Message complet d'une erreur de champ : le résumé du serveur, suivi de sa
 * précision quand elle apporte autre chose.
 *
 * Les deux moitiés portent l'information à tour de rôle : sur un type de
 * fichier refusé, le détail liste les extensions acceptées ; sur une URL non
 * capturable, c'est le résumé qui nomme l'adresse fautive. N'en montrer qu'une
 * perd la moitié du diagnostic selon le cas.
 */
export function fieldMessage(error: unknown, field: string): string | null {
  if (!(error instanceof ApiError)) return null

  const detail = error.fieldError(field)
  if (!detail) return error.message
  if (!error.message || error.message === detail) return detail
  return `${error.message} ${detail}`
}
